import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission, PermissionInput } from '../types';

interface PermissionFormProps {
  editingPermission: Permission | null;
  isSubmitting: boolean;
  isDisabled: boolean;
  onSubmit: (input: PermissionInput) => Promise<boolean>;
  onCancelEdit: () => void;
}

const EMPTY_PERMISSION: PermissionInput = { name: '', description: '' };

const PermissionForm: React.FC<PermissionFormProps> = ({
  editingPermission,
  isSubmitting,
  isDisabled,
  onSubmit,
  onCancelEdit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<PermissionInput>(EMPTY_PERMISSION);

  useEffect(() => {
    setValues(editingPermission
      ? { name: editingPermission.name, description: editingPermission.description }
      : EMPTY_PERMISSION);
  }, [editingPermission]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const didSave = await onSubmit({
      name: values.name.trim(),
      description: values.description.trim(),
    });
    if (didSave) setValues(EMPTY_PERMISSION);
  };

  return (
    <section className="h-full rounded-[20px] border border-outline-variant/70 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-h3 text-h3 text-on-surface">
            {editingPermission
              ? t('admin.settings.permission_form.edit_title')
              : t('admin.settings.permission_form.create_title')}
          </h2>
          <p className="mt-1 text-body-sm text-secondary">
            {t('admin.settings.permission_form.description')}
          </p>
        </div>
        {editingPermission && (
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary/30"
            aria-label={t('admin.settings.actions.cancel_edit')}
            onClick={onCancelEdit}
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        )}
      </div>

      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
          {t('admin.settings.fields.permission_name')}
          <input
            required
            pattern="[A-Z][A-Z0-9_]*"
            title={t('admin.settings.validation.permission_name')}
            className="h-11 rounded-xl border border-outline-variant bg-white px-3 font-body-md text-body-md font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder="SALE_CREATE"
            value={values.name}
            onChange={(event) => setValues((current) => ({
              ...current,
              name: event.target.value.toUpperCase(),
            }))}
          />
        </label>

        <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
          {t('admin.settings.fields.description')}
          <textarea
            required
            rows={3}
            className="resize-y rounded-xl border border-outline-variant bg-white px-3 py-2 font-body-md text-body-md font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            placeholder={t('admin.settings.permission_form.description_placeholder')}
            value={values.description}
            onChange={(event) => setValues((current) => ({
              ...current,
              description: event.target.value,
            }))}
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={isDisabled}
            className="min-h-11 rounded-xl bg-primary px-4 font-label-md text-on-primary transition-colors hover:bg-on-primary-fixed-variant disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting
              ? t('admin.settings.actions.saving')
              : editingPermission
                ? t('admin.settings.actions.update_permission')
                : t('admin.settings.actions.create_permission')}
          </button>
          {editingPermission && (
            <button
              type="button"
              disabled={isDisabled}
              className="min-h-11 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-60"
              onClick={onCancelEdit}
            >
              {t('admin.settings.actions.cancel')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
};

export default PermissionForm;
